/**
 * LiveRegion - Announces changes to screen readers
 *
 * When something changes on the page, screen readers need to know.
 * This component announces updates to blind users.
 *
 * @example
 * <LiveRegion>
 *   Call connected with Netflix support
 * </LiveRegion>
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'

// ============================================
// Types
// ============================================

type Politeness = 'polite' | 'assertive' | 'off'

interface LiveRegionProps {
  children: React.ReactNode
  /** 'polite' waits for user to be idle, 'assertive' interrupts immediately */
  politeness?: Politeness
  /** Unique identifier for multiple regions */
  id?: string
  /** Role for the region */
  role?: 'status' | 'alert' | 'log'
  /** Whether to announce the entire region or just changes */
  atomic?: boolean
  /** Additional CSS class */
  className?: string
}

// ============================================
// LiveRegion Component
// ============================================

export function LiveRegion({
  children,
  politeness = 'polite',
  id,
  role = 'status',
  atomic = true,
  className,
}: LiveRegionProps) {
  return (
    <div
      id={id}
      role={role}
      aria-live={politeness}
      aria-atomic={atomic}
      className={className}
    >
      {children}
    </div>
  )
}

// ============================================
// Announcer Context (for programmatic announcements)
// ============================================

interface AnnouncerContextValue {
  announce: (message: string, politeness?: Politeness) => void
}

const AnnouncerContext = createContext<AnnouncerContextValue | null>(null)

interface AnnouncerProviderProps {
  children: React.ReactNode
}

/**
 * Provider for programmatic screen reader announcements
 *
 * @example
 * // In your app root:
 * <AnnouncerProvider>
 *   <App />
 * </AnnouncerProvider>
 *
 * // In any component:
 * const { announce } = useLiveAnnounce()
 * announce('File uploaded successfully')
 */
export function AnnouncerProvider({ children }: AnnouncerProviderProps) {
  const [politeMessage, setPoliteMessage] = useState('')
  const [assertiveMessage, setAssertiveMessage] = useState('')
  const clearTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const announce = useCallback((message: string, politeness: Politeness = 'polite') => {
    // Clear previous message first (forces re-announcement)
    if (politeness === 'assertive') {
      setAssertiveMessage('')
      setTimeout(() => setAssertiveMessage(message), 50)
    } else {
      setPoliteMessage('')
      setTimeout(() => setPoliteMessage(message), 50)
    }

    // Clear the message after 5 seconds
    if (clearTimeoutRef.current) {
      clearTimeout(clearTimeoutRef.current)
    }
    clearTimeoutRef.current = setTimeout(() => {
      setPoliteMessage('')
      setAssertiveMessage('')
    }, 5000) as unknown as NodeJS.Timeout
  }, [])

  useEffect(() => {
    return () => {
      if (clearTimeoutRef.current) {
        clearTimeout(clearTimeoutRef.current)
      }
    }
  }, [])

  return (
    <AnnouncerContext.Provider value={{ announce }}>
      {children}

      {/* Hidden live regions for announcements */}
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {politeMessage}
      </div>

      <div
        aria-live="assertive"
        aria-atomic="true"
        role="alert"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {assertiveMessage}
      </div>
    </AnnouncerContext.Provider>
  )
}

/**
 * Hook to announce messages to screen readers
 *
 * @example
 * const { announce } = useLiveAnnounce()
 *
 * const handleSubmit = () => {
 *   // ... do something
 *   announce('Form submitted successfully')
 * }
 */
export function useLiveAnnounce() {
  const context = useContext(AnnouncerContext)

  if (!context) {
    // Return a no-op if provider is missing (graceful degradation)
    return {
      announce: (message: string, politeness?: Politeness) => {
        console.warn('AnnouncerProvider not found. Message not announced:', message)
      },
    }
  }

  return context
}
